import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { errorMessage } from '@/lib/errors';
import { enablePush, pushState, syncPushSubscription, type PushState } from '@/lib/push';
import { queryKeys } from '@/lib/query-keys';

/** Where job alerts stand on this phone, and the one tap that turns them on. */
export function usePushAlerts() {
  const [state, setState] = useState<PushState | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void pushState().then((next) => {
      if (live) setState(next);
    });
    return () => {
      live = false;
    };
  }, []);

  const enable = useCallback(async () => {
    if (working) return;
    setWorking(true);
    setError(null);
    try {
      setState(await enablePush());
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setWorking(false);
    }
  }, [working]);

  return { state, working, error, enable };
}

/**
 * The service worker's line back into the open app. Mounted ONCE, in the
 * layout, so it listens on every screen:
 *
 *  - a push that lands while the app is open refreshes the run sheet at once,
 *    instead of on the next half-minute poll;
 *  - a tapped notification the worker could not navigate to asks the app to;
 *  - on launch, an existing subscription is re-sent to the server, which heals
 *    a rotated endpoint or a phone that changed hands.
 */
export function usePushBridge() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    void syncPushSubscription();
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent<{ type?: string; url?: string }>) => {
      if (event.data?.type === 'push') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.runSheet });
      }
      if (event.data?.type === 'open' && event.data.url) {
        const url = new URL(event.data.url, window.location.origin);
        navigate(url.pathname + url.search);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [queryClient, navigate]);
}
